// Sale Pricing - UI-level only, no database changes

interface SalePrice {
  originalPrice: number;
  salePrice: number;
}

interface CartItem {
  name: string;
  price: number;
  quantity: number;
  restaurant_name: string;
}

// PYFC Kitchen discount (10% off all items)
const PYFC_KITCHEN_DISCOUNT_PERCENT = 10;

// Kadi Kadi specific price mappings
const KADI_KADI_PRICE_MAP: { [key: number]: number } = {
  170: 150,  // D170 → D150
  220: 200,  // D220 → D200
};

// Baa Ani Dingo specific price mappings (delivery only)
const BAA_ANI_DINGO_PRICE_MAP: { [key: number]: number } = {
  220: 200,
};

/**
 * Get sale price for PYFC Kitchen products (10% off)
 */
export function getPyfcKitchenSalePrice(
  restaurantName: string,
  originalPrice: number
): SalePrice | null {
  if (!restaurantName.toLowerCase().includes('pyfc')) {
    return null;
  }
  
  const salePrice = Math.round(originalPrice * (1 - PYFC_KITCHEN_DISCOUNT_PERCENT / 100));
  return { originalPrice, salePrice };
}

/**
 * Get sale price for Kadi Kadi products (D170→D150, D220→D200)
 */
export function getKadiKadiSalePrice(
  restaurantName: string,
  originalPrice: number
): SalePrice | null {
  if (!restaurantName.toLowerCase().includes('kadie kadie') && !restaurantName.toLowerCase().includes('kadi kadi')) {
    return null;
  }
  
  const salePrice = KADI_KADI_PRICE_MAP[originalPrice];
  if (salePrice && salePrice < originalPrice) {
    return { originalPrice, salePrice };
  }
  
  return null;
}

/**
 * Tropicana sale removed — prices are back to regular.
 */
export function getTropicanaSalePrice(
  _restaurantName: string,
  _originalPrice: number
): SalePrice | null {
  return null;
}

/**
 * Get sale price for Baa Ani Dingo Restaurant (D220 -> D200, delivery only)
 */
export function getBaaAniDingoSalePrice(
  restaurantName: string,
  originalPrice: number
): SalePrice | null {
  if (!restaurantName.toLowerCase().includes('baa ani dingo')) {
    return null;
  }

  const salePrice = BAA_ANI_DINGO_PRICE_MAP[originalPrice];
  if (salePrice && salePrice < originalPrice) {
    return { originalPrice, salePrice };
  }
  return null;
}

/**
 * Check if a product has a sale price
 */
export function hasSalePrice(
  vendorName: string,
  productName: string,
  categoryName: string,
  originalPrice: number
): boolean {
  return getPyfcKitchenSalePrice(vendorName, originalPrice) !== null ||
         getKadiKadiSalePrice(vendorName, originalPrice) !== null ||
         getBaaAniDingoSalePrice(vendorName, originalPrice) !== null;
}

/**
 * Get the display price (sale price if available, otherwise original)
 */
export function getDisplayPrice(
  vendorName: string,
  productName: string,
  categoryName: string,
  originalPrice: number
): number {
  const pyfcSale = getPyfcKitchenSalePrice(vendorName, originalPrice);
  if (pyfcSale) return pyfcSale.salePrice;
  
  const kadiSale = getKadiKadiSalePrice(vendorName, originalPrice);
  if (kadiSale) return kadiSale.salePrice;
  
  const baaSale = getBaaAniDingoSalePrice(vendorName, originalPrice);
  if (baaSale) return baaSale.salePrice;
  
  return originalPrice;
}

/**
 * Get sale info for any vendor (for display purposes)
 */
export function getAnySalePrice(
  vendorName: string,
  productName: string,
  categoryName: string,
  originalPrice: number
): SalePrice | null {
  const pyfcSale = getPyfcKitchenSalePrice(vendorName, originalPrice);
  if (pyfcSale) return pyfcSale;
  
  const kadiSale = getKadiKadiSalePrice(vendorName, originalPrice);
  if (kadiSale) return kadiSale;
  
  const baaSale = getBaaAniDingoSalePrice(vendorName, originalPrice);
  if (baaSale) return baaSale;
  
  return null;
}

/**
 * Calculate total cart price (no bundle discounts anymore)
 */
export function calculateCartTotalWithBundles(items: CartItem[]): {
  subtotal: number;
  bundleDiscount: number;
  total: number;
  frequenceCount: number;
} {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  return {
    subtotal,
    bundleDiscount: 0,
    total: subtotal,
    frequenceCount: 0
  };
}
